import { ContentType, Folder, IAdvancedFilters, ViewType } from '../types';
import { parseFolder } from '../parsers';
import getPostItems from '../../api/posts-read';
import searchByText from '../../api/search-by-text';
import unlockComments from '../../api/unlock-comments';

import { getSpotItemsFailure, getSpotItemsStart, getSpotItemsSuccess, setFolderCount } from '.';

import { getSpotId } from 'app/scripts/services/app-state';
import { TDispatch, TGetState } from 'state';
import ErrorNotifier, { Severity } from 'app/scripts/services/error-notifier';

const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

const getSpotItems = (contentType: ContentType = 'COMMENTS', withReleaseLock = false) => {
  return async (dispatch: TDispatch, getState: TGetState): Promise<void> => {
    const spotId = getSpotId();
    const {
      pageInfo,
      itemsOnPage: count,
      filters,
      itemsList,
      activeItems,
      isModerationMode,
      unpublishedMessagesOffset
    } = getState().moderation?.messages;
    const { folder, sortBy, offset, tag, viewType, messageType, published, moderatedBy } = pageInfo;
    let fetchSpotItemsApi = getPostItems;
    let advancedFilters: Partial<IAdvancedFilters> = {};

    // In case the viewType is user / conversation / ip,
    // send these filters with rest of the query params
    if (viewType !== ViewType.SPOT) {
      advancedFilters = Object.keys(filters).reduce((prevFilters, filter) => {
        return filters[filter] ? { ...prevFilters, [filter]: filters[filter] } : prevFilters;
      }, {});
    }

    if (viewType === ViewType.TEXT && advancedFilters.text) {
      fetchSpotItemsApi = searchByText;
    }

    if (withReleaseLock) {
      await unlockComments();
      // Requirements from BE to wait for 500ms before fetching items to be sure that the lock is released
      await delay(500);
    }

    dispatch(getSpotItemsStart({ start: activeItems >= itemsList.length, viewType }));
    try {
      const response = await fetchSpotItemsApi({
        contentType,
        spotId,
        folder,
        sortBy,
        count,
        offset,
        tag,
        published,
        lock: isModerationMode && folder === Folder.PENDING,
        advancedFilters,
        unpublishedMessagesOffset,
        messageType,
        moderatedBy
      });

      const { users, messages, posts, expirationTime, count: folderItemsCount, spotPolicy } = parseFolder(
        folder,
        response
      );

      dispatch(
        getSpotItemsSuccess({
          users,
          messages,
          posts,
          folder,
          expirationTime,
          folderItemsCount,
          spotPolicy
        })
      );

      dispatch(setFolderCount({ folderItemsCount }));
    } catch (error) {
      console.error(error);
      ErrorNotifier.notifyError('Failed to fetch moderation items', {
        pageInfo,
        filters,
        level: Severity.Critical
      });
      dispatch(getSpotItemsFailure({ error }));
    }
  };
};

export default getSpotItems;
